import { useEffect, useState, type RefObject } from 'react';
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  PictureInPicture2,
  RotateCcw,
  Loader2,
} from 'lucide-react';
import { usePlayerStore } from '@/store/usePlayerStore';
import { useSettingsStore } from '@/store/useSettingsStore';
import { ProgressBar } from './ProgressBar';
import { LiveButton } from './LiveButton';
import { SettingsMenu } from './SettingsMenu';

interface PlayerControlsProps {
  videoRef: RefObject<HTMLVideoElement | null>;
  containerRef: RefObject<HTMLDivElement | null>;
  visible: boolean;
  isFullscreen: boolean;
}

export function PlayerControls({
  videoRef,
  containerRef,
  visible,
  isFullscreen,
}: PlayerControlsProps) {
  const isPlaying = usePlayerStore((s) => s.isPlaying);
  const triggerReload = usePlayerStore((s) => s.triggerReload);

  const volume = useSettingsStore((s) => s.volume);
  const isMuted = useSettingsStore((s) => s.isMuted);
  const setVolume = useSettingsStore((s) => s.setVolume);
  const toggleMute = useSettingsStore((s) => s.toggleMute);

  const [isBuffering, setIsBuffering] = useState(false);
  const [supportsPiP, setSupportsPiP] = useState(false);
  const [inPiP, setInPiP] = useState(false);

  useEffect(() => {
    setSupportsPiP(
      typeof document !== 'undefined' &&
        'pictureInPictureEnabled' in document &&
        document.pictureInPictureEnabled
    );

    const video = videoRef.current;
    if (!video) return;
    const onEnter = () => setInPiP(true);
    const onLeave = () => setInPiP(false);
    const onWaiting = () => setIsBuffering(true);
    const onPlaying = () => setIsBuffering(false);
    const onCanPlay = () => setIsBuffering(false);
    video.addEventListener('enterpictureinpicture', onEnter);
    video.addEventListener('leavepictureinpicture', onLeave);
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('playing', onPlaying);
    video.addEventListener('canplay', onCanPlay);
    return () => {
      video.removeEventListener('enterpictureinpicture', onEnter);
      video.removeEventListener('leavepictureinpicture', onLeave);
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('canplay', onCanPlay);
    };
  }, [videoRef]);

  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      void video.play();
    } else {
      video.pause();
    }
  };

  const handleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    toggleMute();
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    const video = videoRef.current;
    if (video) {
      video.volume = v;
      if (v > 0 && video.muted) video.muted = false;
    }
  };

  const toggleFullscreen = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await containerRef.current?.requestFullscreen();
      }
    } catch {
      // Some browsers reject fullscreen without user gesture in certain contexts.
    }
  };

  const togglePiP = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else {
        await video.requestPictureInPicture();
      }
    } catch {
      // PiP denied.
    }
  };

  const handleReload = (e: React.MouseEvent) => {
    e.stopPropagation();
    triggerReload();
  };

  const VolumeIcon = isMuted || volume === 0 ? VolumeX : Volume2;

  return (
    <div
      className={`absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/90 via-black/50 to-transparent pt-6 pb-2 px-1 transition-opacity duration-200 ${
        visible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Progress Bar + Time */}
      <ProgressBar videoRef={videoRef} visible={visible} />

      {/* Control buttons row */}
      <div className="flex items-center gap-1.5 text-white mt-1">
        {/* Play / Pause */}
        <button
          onClick={togglePlay}
          aria-label={isPlaying ? 'Pause' : 'Play'}
          className="p-1.5 rounded hover:bg-white/10 transition-colors"
        >
          {isBuffering ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : isPlaying ? (
            <Pause className="w-5 h-5" />
          ) : (
            <Play className="w-5 h-5" />
          )}
        </button>

        {/* Reload */}
        <button
          onClick={handleReload}
          aria-label="Reload stream"
          title="Reload stream"
          className="p-1.5 rounded hover:bg-white/10 transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
        </button>

        {/* Volume */}
        <div className="relative flex items-center group">
          <button
            onClick={handleMute}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
            className="p-1.5 rounded hover:bg-white/10 transition-colors"
          >
            <VolumeIcon className="w-5 h-5" />
          </button>
          <div className="overflow-hidden transition-all duration-200 w-20 opacity-100 ml-1 md:w-0 md:opacity-0 md:ml-0 md:group-hover:w-20 md:group-hover:opacity-100 md:group-hover:ml-1">
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={isMuted ? 0 : volume}
              onChange={handleVolumeChange}
              aria-label="Volume"
              className="w-full h-1 accent-accent"
            />
          </div>
        </div>

        <div className="flex-1" />

        {/* Live indicator */}
        <LiveButton />

        {/* Settings */}
        <SettingsMenu videoRef={videoRef} />

        {/* Picture-in-Picture */}
        {supportsPiP && (
          <button
            onClick={togglePiP}
            aria-label={inPiP ? 'Exit picture-in-picture' : 'Picture-in-picture'}
            title="Picture-in-picture"
            className={`p-1.5 rounded hover:bg-white/10 transition-colors ${inPiP ? 'text-accent' : ''}`}
          >
            <PictureInPicture2 className="w-4 h-4" />
          </button>
        )}

        {/* Fullscreen */}
        <button
          onClick={toggleFullscreen}
          aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          title="Fullscreen"
          className="p-1.5 rounded hover:bg-white/10 transition-colors"
        >
          {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
